import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import {
  Save as SaveIcon,
  RestoreOutlined as RestoreIcon,
  Palette as PaletteIcon,
  TextFields as TextIcon,
  TableChart as TableIcon
} from '@mui/icons-material';
import { getExcelTemplate, getPdfTemplate } from '../templates/export/excelConfig';
import toast from 'react-hot-toast';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function ConfiguracionPlantillas() {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);

  // Configuración editable
  const [config, setConfig] = useState({
    empresa: localStorage.getItem('empresa_nombre') || 'Mi Empresa S.A. de C.V.',
    telefono: localStorage.getItem('empresa_telefono') || '',
    email: localStorage.getItem('empresa_email') || '',
    web: localStorage.getItem('empresa_web') || '',
    direccion: localStorage.getItem('empresa_direccion') || '',
    mostrarTelefono: localStorage.getItem('mostrar_telefono') !== 'false',
    mostrarEmail: localStorage.getItem('mostrar_email') !== 'false',
    mostrarWeb: localStorage.getItem('mostrar_web') !== 'false',
    mostrarDireccion: localStorage.getItem('mostrar_direccion') !== 'false',
    colores: {
      principal: localStorage.getItem('color_principal') || '#1976d2',
      secundario: localStorage.getItem('color_secundario') || '#dc004e',
      acento: localStorage.getItem('color_acento') || '#ff9800'
    },
    margenes: {
      superior: parseInt(localStorage.getItem('margen_superior') || '20'),
      inferior: parseInt(localStorage.getItem('margen_inferior') || '20'),
      izquierdo: parseInt(localStorage.getItem('margen_izquierdo') || '20'),
      derecho: parseInt(localStorage.getItem('margen_derecho') || '20')
    },
    columnas: {
      codigo: localStorage.getItem('col_codigo') === 'true',
      descripcion: true,
      unidad: localStorage.getItem('col_unidad') !== 'false',
      cantidad: localStorage.getItem('col_cantidad') !== 'false',
      precioUnitario: localStorage.getItem('col_pu') !== 'false',
      importe: localStorage.getItem('col_importe') !== 'false'
    },
    encabezado: {
      mostrarLogo: localStorage.getItem('mostrar_logo') === 'true',
      logoUrl: localStorage.getItem('logo_url') || '',
      incluirFecha: localStorage.getItem('incluir_fecha') !== 'false',
      incluirFolio: localStorage.getItem('incluir_folio') !== 'false'
    },
    totales: {
      mostrarIndirectos: localStorage.getItem('mostrar_indirectos') !== 'false',
      mostrarFinanciamiento: localStorage.getItem('mostrar_financiamiento') !== 'false',
      mostrarUtilidad: localStorage.getItem('mostrar_utilidad') !== 'false',
      mostrarIVA: localStorage.getItem('mostrar_iva') !== 'false'
    }
  });

  const handleSave = () => {
    // Guardar en localStorage
    localStorage.setItem('empresa_nombre', config.empresa);
    localStorage.setItem('empresa_telefono', config.telefono);
    localStorage.setItem('empresa_email', config.email);
    localStorage.setItem('empresa_web', config.web);
    localStorage.setItem('empresa_direccion', config.direccion);
    localStorage.setItem('mostrar_telefono', config.mostrarTelefono);
    localStorage.setItem('mostrar_email', config.mostrarEmail);
    localStorage.setItem('mostrar_web', config.mostrarWeb);
    localStorage.setItem('mostrar_direccion', config.mostrarDireccion);
    localStorage.setItem('color_principal', config.colores.principal);
    localStorage.setItem('color_secundario', config.colores.secundario);
    localStorage.setItem('color_acento', config.colores.acento);
    localStorage.setItem('margen_superior', config.margenes.superior);
    localStorage.setItem('margen_inferior', config.margenes.inferior);
    localStorage.setItem('margen_izquierdo', config.margenes.izquierdo);
    localStorage.setItem('margen_derecho', config.margenes.derecho);
    localStorage.setItem('col_codigo', config.columnas.codigo);
    localStorage.setItem('col_unidad', config.columnas.unidad);
    localStorage.setItem('col_cantidad', config.columnas.cantidad);
    localStorage.setItem('col_pu', config.columnas.precioUnitario);
    localStorage.setItem('col_importe', config.columnas.importe);
    localStorage.setItem('mostrar_logo', config.encabezado.mostrarLogo);
    localStorage.setItem('logo_url', config.encabezado.logoUrl);
    localStorage.setItem('incluir_fecha', config.encabezado.incluirFecha);
    localStorage.setItem('incluir_folio', config.encabezado.incluirFolio);
    localStorage.setItem('mostrar_indirectos', config.totales.mostrarIndirectos);
    localStorage.setItem('mostrar_financiamiento', config.totales.mostrarFinanciamiento);
    localStorage.setItem('mostrar_utilidad', config.totales.mostrarUtilidad);
    localStorage.setItem('mostrar_iva', config.totales.mostrarIVA);

    toast.success('Configuración guardada exitosamente');

    // Regresar a la página anterior después de guardar
    setTimeout(() => {
      navigate(-1);
    }, 1000);
  };

  const handleRestore = () => {
    // Limpiar localStorage
    const keysToRemove = [
      'empresa_nombre', 'empresa_telefono', 'empresa_email', 'empresa_web', 'empresa_direccion',
      'mostrar_telefono', 'mostrar_email', 'mostrar_web', 'mostrar_direccion',
      'color_principal', 'color_secundario', 'color_acento',
      'margen_superior', 'margen_inferior', 'margen_izquierdo', 'margen_derecho',
      'col_codigo', 'col_unidad', 'col_cantidad', 'col_pu', 'col_importe',
      'mostrar_logo', 'logo_url', 'incluir_fecha', 'incluir_folio',
      'mostrar_indirectos', 'mostrar_financiamiento', 'mostrar_utilidad', 'mostrar_iva'
    ];

    keysToRemove.forEach(key => localStorage.removeItem(key));

    // Restaurar valores por defecto
    setConfig({
      empresa: 'Mi Empresa S.A. de C.V.',
      telefono: '',
      email: '',
      web: '',
      direccion: '',
      mostrarTelefono: true,
      mostrarEmail: true,
      mostrarWeb: true,
      mostrarDireccion: true,
      colores: {
        principal: '#1976d2',
        secundario: '#dc004e',
        acento: '#ff9800'
      },
      margenes: {
        superior: 20,
        inferior: 20,
        izquierdo: 20,
        derecho: 20
      },
      columnas: {
        codigo: true,
        descripcion: true,
        unidad: true,
        cantidad: true,
        precioUnitario: true,
        importe: true
      },
      encabezado: {
        mostrarLogo: false,
        logoUrl: '',
        incluirFecha: true,
        incluirFolio: true
      },
      totales: {
        mostrarIndirectos: true,
        mostrarFinanciamiento: true,
        mostrarUtilidad: true,
        mostrarIVA: true
      }
    });

    toast.success('Configuración restaurada a valores por defecto');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Configuración General de Exportación
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Configura los datos de tu empresa y preferencias generales. Estos datos se aplicarán a todas las plantillas.
        Para modificar el diseño de las plantillas, usa el Diseñador de Plantillas.
      </Alert>

      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => navigate('/gestion-plantillas')}
          startIcon={<PaletteIcon />}
        >
          Gestionar Plantillas
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => navigate('/diseñador-plantillas')}
          startIcon={<TableIcon />}
        >
          Diseñador Visual (Beta)
        </Button>
      </Box>

      <Paper sx={{ width: '100%', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab icon={<TextIcon />} label="Datos de Empresa" />
          <Tab icon={<PaletteIcon />} label="Colores y Estilos" />
          <Tab icon={<TableIcon />} label="Opciones de Exportación" />
        </Tabs>
      </Paper>

      <Paper sx={{ p: 3 }}>
        {/* Tab 1: Datos de Empresa */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Datos de la Empresa
                  </Typography>
                  <TextField
                    fullWidth
                    label="Nombre de la Empresa"
                    value={config.empresa}
                    onChange={(e) => setConfig({ ...config, empresa: e.target.value })}
                    margin="normal"
                  />

                  <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.mostrarTelefono}
                          onChange={(e) => setConfig({ ...config, mostrarTelefono: e.target.checked })}
                        />
                      }
                      label="Teléfono"
                    />
                    {config.mostrarTelefono && (
                      <TextField
                        fullWidth
                        label="Teléfono"
                        value={config.telefono}
                        onChange={(e) => setConfig({ ...config, telefono: e.target.value })}
                        margin="normal"
                        size="small"
                      />
                    )}
                  </Box>

                  <Box sx={{ mt: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.mostrarEmail}
                          onChange={(e) => setConfig({ ...config, mostrarEmail: e.target.checked })}
                        />
                      }
                      label="Email"
                    />
                    {config.mostrarEmail && (
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={config.email}
                        onChange={(e) => setConfig({ ...config, email: e.target.value })}
                        margin="normal"
                        size="small"
                      />
                    )}
                  </Box>

                  <Box sx={{ mt: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.mostrarWeb}
                          onChange={(e) => setConfig({ ...config, mostrarWeb: e.target.checked })}
                        />
                      }
                      label="Página Web"
                    />
                    {config.mostrarWeb && (
                      <TextField
                        fullWidth
                        label="Página Web"
                        value={config.web}
                        onChange={(e) => setConfig({ ...config, web: e.target.value })}
                        margin="normal"
                        size="small"
                      />
                    )}
                  </Box>

                  <Box sx={{ mt: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.mostrarDireccion}
                          onChange={(e) => setConfig({ ...config, mostrarDireccion: e.target.checked })}
                        />
                      }
                      label="Dirección"
                    />
                    {config.mostrarDireccion && (
                      <TextField
                        fullWidth
                        label="Dirección"
                        multiline
                        rows={2}
                        value={config.direccion}
                        onChange={(e) => setConfig({ ...config, direccion: e.target.value })}
                        margin="normal"
                        size="small"
                      />
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.encabezado.mostrarLogo}
                        onChange={(e) => setConfig({
                          ...config,
                          encabezado: { ...config.encabezado, mostrarLogo: e.target.checked }
                        })}
                      />
                    }
                    label="Mostrar Logo en Exportaciones"
                  />
                  {config.encabezado.mostrarLogo && (
                    <TextField
                      fullWidth
                      label="URL del Logo"
                      value={config.encabezado.logoUrl}
                      onChange={(e) => setConfig({
                        ...config,
                        encabezado: { ...config.encabezado, logoUrl: e.target.value }
                      })}
                      margin="normal"
                      helperText="URL de la imagen o ruta local"
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Opciones del Encabezado
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.encabezado.incluirFecha}
                        onChange={(e) => setConfig({
                          ...config,
                          encabezado: { ...config.encabezado, incluirFecha: e.target.checked }
                        })}
                      />
                    }
                    label="Incluir Fecha"
                  />
                  <br />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.encabezado.incluirFolio}
                        onChange={(e) => setConfig({
                          ...config,
                          encabezado: { ...config.encabezado, incluirFolio: e.target.checked }
                        })}
                      />
                    }
                    label="Incluir Folio/Código"
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 2: Colores y Estilos */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Colores Corporativos
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, my: 2 }}>
                    <Box>
                      <Typography variant="caption">Color Principal</Typography>
                      <input
                        type="color"
                        value={config.colores.principal}
                        onChange={(e) => setConfig({
                          ...config,
                          colores: { ...config.colores, principal: e.target.value }
                        })}
                        style={{ width: 80, height: 40, cursor: 'pointer', display: 'block' }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="caption">Color Secundario</Typography>
                      <input
                        type="color"
                        value={config.colores.secundario}
                        onChange={(e) => setConfig({
                          ...config,
                          colores: { ...config.colores, secundario: e.target.value }
                        })}
                        style={{ width: 80, height: 40, cursor: 'pointer', display: 'block' }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="caption">Color Acento</Typography>
                      <input
                        type="color"
                        value={config.colores.acento}
                        onChange={(e) => setConfig({
                          ...config,
                          colores: { ...config.colores, acento: e.target.value }
                        })}
                        style={{ width: 80, height: 40, cursor: 'pointer', display: 'block' }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Márgenes (PDF)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Superior (mm)"
                        type="number"
                        value={config.margenes.superior}
                        onChange={(e) => setConfig({
                          ...config,
                          margenes: { ...config.margenes, superior: parseInt(e.target.value) }
                        })}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Inferior (mm)"
                        type="number"
                        value={config.margenes.inferior}
                        onChange={(e) => setConfig({
                          ...config,
                          margenes: { ...config.margenes, inferior: parseInt(e.target.value) }
                        })}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Izquierdo (mm)"
                        type="number"
                        value={config.margenes.izquierdo}
                        onChange={(e) => setConfig({
                          ...config,
                          margenes: { ...config.margenes, izquierdo: parseInt(e.target.value) }
                        })}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Derecho (mm)"
                        type="number"
                        value={config.margenes.derecho}
                        onChange={(e) => setConfig({
                          ...config,
                          margenes: { ...config.margenes, derecho: parseInt(e.target.value) }
                        })}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 3: Columnas y Campos */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Columnas Visibles
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.columnas.codigo}
                        onChange={(e) => setConfig({
                          ...config,
                          columnas: { ...config.columnas, codigo: e.target.checked }
                        })}
                      />
                    }
                    label="Código"
                  />
                  <br />
                  <FormControlLabel
                    control={<Switch checked disabled />}
                    label="Descripción (Requerido)"
                  />
                  <br />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.columnas.unidad}
                        onChange={(e) => setConfig({
                          ...config,
                          columnas: { ...config.columnas, unidad: e.target.checked }
                        })}
                      />
                    }
                    label="Unidad"
                  />
                  <br />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.columnas.cantidad}
                        onChange={(e) => setConfig({
                          ...config,
                          columnas: { ...config.columnas, cantidad: e.target.checked }
                        })}
                      />
                    }
                    label="Cantidad"
                  />
                  <br />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.columnas.precioUnitario}
                        onChange={(e) => setConfig({
                          ...config,
                          columnas: { ...config.columnas, precioUnitario: e.target.checked }
                        })}
                      />
                    }
                    label="Precio Unitario"
                  />
                  <br />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.columnas.importe}
                        onChange={(e) => setConfig({
                          ...config,
                          columnas: { ...config.columnas, importe: e.target.checked }
                        })}
                      />
                    }
                    label="Importe"
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Sección de Totales
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.totales.mostrarIndirectos}
                        onChange={(e) => setConfig({
                          ...config,
                          totales: { ...config.totales, mostrarIndirectos: e.target.checked }
                        })}
                      />
                    }
                    label="Mostrar Indirectos"
                  />
                  <br />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.totales.mostrarFinanciamiento}
                        onChange={(e) => setConfig({
                          ...config,
                          totales: { ...config.totales, mostrarFinanciamiento: e.target.checked }
                        })}
                      />
                    }
                    label="Mostrar Financiamiento"
                  />
                  <br />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.totales.mostrarUtilidad}
                        onChange={(e) => setConfig({
                          ...config,
                          totales: { ...config.totales, mostrarUtilidad: e.target.checked }
                        })}
                      />
                    }
                    label="Mostrar Utilidad"
                  />
                  <br />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.totales.mostrarIVA}
                        onChange={(e) => setConfig({
                          ...config,
                          totales: { ...config.totales, mostrarIVA: e.target.checked }
                        })}
                      />
                    }
                    label="Mostrar IVA"
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Botones de acción */}
        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            startIcon={<RestoreIcon />}
            onClick={handleRestore}
            color="secondary"
          >
            Restaurar Valores por Defecto
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            color="primary"
          >
            Guardar Configuración
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

export default ConfiguracionPlantillas;